import { useRecoveryStore } from '@/state/useRecoveryStore';
import { formatDateTime } from '@/lib/format';

export function TransactionRecoveryDialog(): JSX.Element | null {
  const open = useRecoveryStore((s) => s.open);
  const status = useRecoveryStore((s) => s.status);
  const resume = useRecoveryStore((s) => s.resume);
  const clearStale = useRecoveryStore((s) => s.clearStale);
  const decideLater = useRecoveryStore((s) => s.decideLater);

  if (status !== 'scanned' || open.length === 0) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tx-recovery-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
      data-testid="tx-recovery-dialog"
    >
      <div className="max-w-lg w-full rounded-lg border border-amber-700 bg-slate-900 p-6 shadow-2xl">
        <h2 id="tx-recovery-title" className="text-lg font-semibold text-amber-100">
          Open transactions found
        </h2>
        <p className="mt-1 text-xs text-slate-400">
          These transactions were active when the tab last closed. Resume them to send a normal
          StopTransaction to the CSMS, or clear them as stale (kept for audit, never sent).
        </p>
        <ul className="mt-4 space-y-2 text-sm">
          {open.map((tx) => (
            <li
              key={tx.id}
              className="flex items-center justify-between gap-3 rounded bg-slate-800 px-3 py-2"
              data-testid={`recovery-row-${tx.id}`}
            >
              <div className="text-xs">
                <div className="font-mono text-slate-100">
                  tx#{String(tx.id)} · connector {String(tx.connectorId)} · {tx.idTag}
                </div>
                <div className="text-slate-500">
                  started {formatDateTime(tx.startedAt)}
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => {
                    void resume(tx.id);
                  }}
                  className="rounded bg-primary-base px-2 py-1 text-xs text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base"
                  data-testid={`recovery-resume-${tx.id}`}
                >
                  Resume
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void clearStale(tx.id);
                  }}
                  className="rounded bg-rose-700 px-2 py-1 text-xs text-rose-50 hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-300"
                  data-testid={`recovery-clear-${tx.id}`}
                >
                  Clear stale
                </button>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-4 text-right">
          <button
            type="button"
            onClick={decideLater}
            className="rounded bg-slate-700 px-3 py-1 text-xs text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
            data-testid="recovery-decide-later"
          >
            Decide later
          </button>
        </div>
      </div>
    </div>
  );
}
